import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader, StatCard } from '../components/common';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { AccountBalanceWallet, TrendingUp, Download } from '@mui/icons-material';
import { useFinanceReports, useFinancialPeriods, usePermission } from '../hooks';
import { colors } from '../theme/theme';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export const FinanceReportsPage = () => {
  const { can } = usePermission();
  const [selectedPeriodId, setSelectedPeriodId] = useState<number | undefined>(undefined);
  const { reports, isLoading } = useFinanceReports(selectedPeriodId);
  const { periods } = useFinancialPeriods({ per_page: 100 });

  const period = reports?.period;
  const allPeriods = reports?.periods || [];
  const reportData = reports?.reportData || [];

  // Calculate totals
  const totalTarget = reportData.reduce((sum: number, item: any) => sum + parseFloat(item.target || 0), 0);
  const totalCollected = reportData.reduce((sum: number, item: any) => sum + parseFloat(item.collected || 0), 0);
  const collectionEfficiency = totalTarget > 0 ? ((totalCollected / totalTarget) * 100).toFixed(1) : '0';
  const variance = totalTarget > 0 ? totalCollected - totalTarget : 0;

  const handleExportCSV = () => {
    if (!reportData.length) return;
    const headers = ['Revenue Stream', 'Services', 'Target Amount', 'Collected', 'Variance', 'Achievement %'];
    const rows = reportData.map((row: any) => [
      row.stream,
      row.services_count,
      parseFloat(row.target || 0).toFixed(2),
      parseFloat(row.collected || 0).toFixed(2),
      (parseFloat(row.collected || 0) - parseFloat(row.target || 0)).toFixed(2),
      parseFloat(row.progress || 0).toFixed(1),
    ]);
    rows.push(['Total', '', totalTarget.toFixed(2), totalCollected.toFixed(2), variance.toFixed(2), collectionEfficiency]);

    const csv = [headers.join(','), ...rows.map((r: any) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `finance-report-${period?.name || 'report'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: GridColDef[] = [
    {
      field: 'stream',
      headerName: 'Revenue Stream',
      flex: 1,
      minWidth: 200,
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'services_count',
      headerName: 'Services',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      display: 'flex',
    },
    {
      field: 'target',
      headerName: 'Target Amount',
      width: 170,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2">
          KES {parseFloat(params.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      ),
    },
    {
      field: 'collected',
      headerName: 'Collected',
      width: 170,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: colors.success.main }}>
          KES {parseFloat(params.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      ),
    },
    {
      field: 'variance',
      headerName: 'Variance',
      width: 170,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params) => {
        const target = parseFloat(params.row.target || 0);
        const collected = parseFloat(params.row.collected || 0);
        const v = collected - target;
        return (
          <Typography variant="body2" fontWeight={600} sx={{ color: v >= 0 ? colors.success.main : colors.error.main }}>
            {v >= 0 ? '+' : ''}{v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        );
      },
    },
    {
      field: 'progress',
      headerName: 'Achievement',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      display: 'flex',
      renderCell: (params) => {
        const value = parseFloat(params.value || 0);
        const color = value >= 85 ? colors.success.main : value >= 70 ? colors.warning.main : colors.error.main;
        return (
          <Chip
            label={`${value.toFixed(1)}%`}
            size="small"
            sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: `${color}15`, color }}
          />
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Finance Performance Reports"
          subtitle={period ? `Period: ${period.name}` : 'Revenue performance and analytics'}
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Finance & Budget', path: '/finance/dashboard' },
            { label: 'Reports' },
          ]}
        />

        {/* Period Selector */}
        {allPeriods.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 300 }}>
                  <InputLabel>Financial Period</InputLabel>
                  <Select
                    value={selectedPeriodId || period?.id || ''}
                    label="Financial Period"
                    onChange={(e) => setSelectedPeriodId(e.target.value as number)}
                  >
                    {allPeriods.map((p: any) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name} {p.status === 'active' ? '(Active)' : `(${p.status})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {reportData.length > 0 && can('reports.export') && (
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleExportCSV}
                    size="small"
                  >
                    Export CSV
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {period ? (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <StatCard
                  title="Total Target"
                  value={`KES ${totalTarget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  icon={<AccountBalanceWallet />}
                  color={colors.primary.main}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <StatCard
                  title="Total Collections"
                  value={`KES ${totalCollected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  icon={<TrendingUp />}
                  color={colors.success.main}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <StatCard
                  title="Collection Efficiency"
                  value={`${collectionEfficiency}%`}
                  icon={<TrendingUp />}
                  color={parseFloat(collectionEfficiency) >= 70 ? colors.success.main : colors.warning.main}
                />
              </Grid>
            </Grid>
          </>
        ) : (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" sx={{ mb: 1, color: colors.grey[800] }}>
                No Active Financial Period
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Create a financial period and revenue streams to view performance reports
              </Typography>
            </CardContent>
          </Card>
        )}

        {period && (
          <>
            {/* Revenue Streams Performance Table */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 3, pb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Revenue Streams Performance
                  </Typography>
                </Box>
                <Box sx={{ height: 500, width: '100%' }}>
                  <DataGrid
                    rows={reportData}
                    columns={columns}
                    getRowId={(row) => `${row.stream}-${row.target}-${row.collected}`}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                      pagination: {
                        paginationModel: { pageSize: 10 },
                      },
                    }}
                    disableRowSelectionOnClick
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-cell': {
                        borderBottom: `1px solid ${colors.grey[200]}`,
                        py: 2,
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: colors.grey[50],
                        borderBottom: `2px solid ${colors.grey[300]}`,
                        borderTop: `1px solid ${colors.grey[200]}`,
                      },
                      '& .MuiDataGrid-footerContainer': {
                        borderTop: `2px solid ${colors.grey[300]}`,
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Revenue Streams Performance Chart */}
            <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: colors.grey[800] }}>
                  Target vs Collected Comparison
                </Typography>
                {reportData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={380}>
                    <BarChart data={reportData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <defs>
                        <linearGradient id="targetGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={colors.primary.main} stopOpacity={0.9}/>
                          <stop offset="95%" stopColor={colors.primary.main} stopOpacity={0.7}/>
                        </linearGradient>
                        <linearGradient id="collectedGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={colors.success.main} stopOpacity={0.9}/>
                          <stop offset="95%" stopColor={colors.success.main} stopOpacity={0.7}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[200]} vertical={false} />
                      <XAxis
                        dataKey="stream"
                        stroke={colors.grey[600]}
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                      />
                      <YAxis
                        stroke={colors.grey[600]}
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        formatter={(value: any) => `KES ${parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: `1px solid ${colors.grey[300]}`,
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          padding: '12px'
                        }}
                        cursor={{ fill: colors.grey[100] }}
                      />
                      <Legend
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="circle"
                      />
                      <Bar
                        dataKey="target"
                        name="Target"
                        fill="url(#targetGradient)"
                        radius={[8, 8, 0, 0]}
                        maxBarSize={70}
                      />
                      <Bar
                        dataKey="collected"
                        name="Collected"
                        fill="url(#collectedGradient)"
                        radius={[8, 8, 0, 0]}
                        maxBarSize={70}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{
                    height: 380,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: colors.grey[50],
                    borderRadius: 2,
                  }}>
                    <Typography color="text.secondary">No revenue streams with targets configured</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    </AdminLayout>
  );
};
